import React from 'react';
import { motion } from 'framer-motion';
import { FaSpinner, FaCheck, FaExclamationTriangle } from 'react-icons/fa';

interface LoadingProgressProps {
  progress: number;
  stage: string;
  isVisible: boolean;
  onComplete?: () => void;
  currentLanguage: 'ar' | 'en';
}

const stages = {
  ar: {
    starting: 'بدء التحليل...',
    analyzing: 'تحليل البيانات...',
    fields: 'تحليل الحقول...',
    relationships: 'تحليل العلاقات...',
    statistics: 'حساب الإحصائيات...',
    complete: 'التحليل مكتمل!'
  },
  en: {
    starting: 'Starting analysis...',
    analyzing: 'Analyzing data...',
    fields: 'Analyzing fields...',
    relationships: 'Analyzing relationships...',
    statistics: 'Calculating statistics...',
    complete: 'Analysis complete!'
  }
};

export default function LoadingProgress({ 
  progress, 
  stage, 
  isVisible, 
  onComplete,
  currentLanguage 
}: LoadingProgressProps) {
  const t = stages[currentLanguage];
  
  React.useEffect(() => {
    if (progress >= 100 && onComplete) {
      const timer = setTimeout(onComplete, 1000);
      return () => clearTimeout(timer);
    }
  }, [progress, onComplete]);

  if (!isVisible) return null;

  const getStageIcon = () => {
    if (progress >= 100) return <FaCheck className="w-5 h-5 text-green-500" />;
    if (progress < 0) return <FaExclamationTriangle className="w-5 h-5 text-red-500" />;
    return <FaSpinner className="w-5 h-5 text-blue-500 animate-spin" />;
  };

  const getProgressColor = () => {
    if (progress >= 100) return 'bg-green-500';
    if (progress >= 80) return 'bg-blue-500';
    if (progress >= 60) return 'bg-indigo-500';
    if (progress >= 40) return 'bg-purple-500';
    if (progress >= 20) return 'bg-pink-500';
    return 'bg-red-500';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-2xl max-w-md w-full mx-4"
      >
        <div className="text-center space-y-6">
          {/* الأيقونة */}
          <motion.div
            animate={{ rotate: progress < 100 ? 360 : 0 }}
            transition={{ duration: 1, repeat: progress < 100 ? Infinity : 0, ease: "linear" }}
            className="flex justify-center"
          >
            {getStageIcon()}
          </motion.div>

          {/* العنوان */}
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {currentLanguage === 'ar' ? 'تحليل البيانات' : 'Data Analysis'}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {stage}
            </p>
          </div>

          {/* شريط التقدم */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-300">
                {currentLanguage === 'ar' ? 'التقدم' : 'Progress'}
              </span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {Math.round(progress)}%
              </span>
            </div>
            
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
              <motion.div
                className={`h-full ${getProgressColor()} rounded-full`}
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
          </div>

          {/* تفاصيل المرحلة */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  {currentLanguage === 'ar' ? 'المرحلة الحالية:' : 'Current Stage:'}
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {stage}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  {currentLanguage === 'ar' ? 'الوقت المقدر:' : 'Estimated Time:'}
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {progress < 50 ? 
                    (currentLanguage === 'ar' ? 'أقل من دقيقة' : '< 1 min') :
                    (currentLanguage === 'ar' ? 'أقل من 30 ثانية' : '< 30 sec')
                  }
                </span>
              </div>
            </div>
          </div>

          {/* رسالة التشجيع */}
          {progress < 100 && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-sm text-gray-500 dark:text-gray-400"
            >
              {currentLanguage === 'ar' 
                ? 'يرجى الانتظار، جاري تحليل البيانات...'
                : 'Please wait, analyzing your data...'
              }
            </motion.p>
          )}

          {/* رسالة الإكمال */}
          {progress >= 100 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <div className="text-green-500 text-2xl mb-2">🎉</div>
              <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                {currentLanguage === 'ar' 
                  ? 'تم تحليل البيانات بنجاح!'
                  : 'Data analysis completed successfully!'
                }
              </p>
            </motion.div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
} 